import { useState } from "react";
import { Send, Loader2, Store, IndianRupee, Handshake, Users, ShieldCheck } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";
import { toast } from "sonner";

const BENEFITS = [
  { icon: Store, t: "Run a TASSEI Centre", d: "Open a TASSEI-branded centre in your city with our full curriculum, branding and operations playbook." },
  { icon: IndianRupee, t: "Strong unit economics", d: "Multiple revenue streams — coaching, computer education, AI programs, admissions counselling." },
  { icon: Handshake, t: "Ongoing support", d: "Marketing collateral, lead supply, faculty training and tech platform — all included." },
  { icon: ShieldCheck, t: "Proven brand promise", d: "Lifetime free practice, job assistance, industry mentors — all standardised across centres." },
];

export default function Partner() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "",
    current_business: "", investment_capacity: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.city) {
      toast.error("Please fill name, email, phone and city.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/partner", form);
      toast.success("Thanks! Our partnership team will contact you shortly.");
      setForm({ name: "", email: "", phone: "", city: "", current_business: "", investment_capacity: "", message: "" });
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="partner-page">
      <PageHero
        eyebrow="TASSEI Partner Program"
        title="Become a TASSEI franchise. Build careers in your city."
        desc="Bring TASSEI's career-success ecosystem to your hometown — coaching, AI programs, university admissions and placement, under one trusted brand."
        image="https://images.pexels.com/photos/3184398/pexels-photo-3184398.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1920"
        alt="TASSEI franchise partner"
      />

      <section className="pt-16 pb-12" data-testid="partner-benefits-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {BENEFITS.map((v, idx) => {
            const Icon = v.icon;
            return (
              <div key={idx} data-testid={`partner-benefit-${idx}`} className="bg-white rounded-2xl border border-slate-200 p-5 hover:-translate-y-1 hover:shadow-lg transition-all">
                <div className="w-11 h-11 rounded-xl bg-[#00B37E]/12 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#00B37E]" />
                </div>
                <div className="mt-4 font-heading text-base font-bold text-[#0A192F]">{v.t}</div>
                <div className="text-sm text-[#64748B] mt-1.5 leading-relaxed">{v.d}</div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <form
            onSubmit={submit}
            data-testid="partner-form"
            className="bg-white border border-slate-200 rounded-3xl p-7 md:p-10"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#0A192F] flex items-center justify-center">
                <Users className="w-6 h-6 text-[#00B37E]" />
              </div>
              <div>
                <h3 className="font-heading text-2xl font-bold text-[#0A192F]">Apply for franchise</h3>
                <p className="text-sm text-[#64748B] mt-0.5">Share a few details — our partnerships team will follow up.</p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pname">Full Name *</Label>
                <Input id="pname" data-testid="partner-name" value={form.name} onChange={set("name")} placeholder="Your name" required className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="pphone">Phone *</Label>
                <Input id="pphone" data-testid="partner-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." required className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="pemail">Email *</Label>
                <Input id="pemail" data-testid="partner-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" required className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="pcity">City *</Label>
                <Input id="pcity" data-testid="partner-city" value={form.city} onChange={set("city")} placeholder="Lucknow, Patna, Indore..." required className="mt-1.5" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="pcb">Current business / occupation</Label>
                <Input id="pcb" data-testid="partner-business" value={form.current_business} onChange={set("current_business")} placeholder="e.g. Run a coaching centre, Govt job" className="mt-1.5" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="pic">Investment capacity</Label>
                <Input id="pic" data-testid="partner-investment" value={form.investment_capacity} onChange={set("investment_capacity")} placeholder="e.g. ₹5-10 Lakh" className="mt-1.5" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="pmsg">Anything else?</Label>
                <Textarea id="pmsg" data-testid="partner-message" value={form.message} onChange={set("message")} rows={4} placeholder="Tell us about your vision for TASSEI in your city" className="mt-1.5" />
              </div>
            </div>

            <Button type="submit" disabled={loading} data-testid="partner-submit" className="mt-7 bg-[#00B37E] hover:bg-[#009166] text-white font-semibold rounded-full h-12 px-7">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {loading ? "Submitting..." : "Submit Application"}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
